import { useEffect, useState } from "react";
import type { DoorSettings, DoorSettingsInput, FieldIssue } from "../../lib/api";
import { LinkButton, SecondaryButton } from "../ui/button";

/**
 * The door's written settings, as ONE form with ONE Save.
 *
 * **THERE IS NO PRICE FIELD AND THERE CANNOT BE ONE** (ruling C3,
 * economics-wire §6.3). A door owner has no price control: there are no cheap
 * doors and no expensive doors, and what varies is how much work a given knock
 * asks for, which is a property of the knock rather than of the door it arrived
 * at. The input, its floor sentence and its validation copy are deleted
 * outright — the api ignores the field, so a form that still PATCHed it would be
 * a control that visibly did nothing.
 *
 * Why one form and not live controls: what is here is words somebody types, and
 * a field that saves per keystroke saves half a sentence. So it is drafted and
 * sent deliberately, and the button says so by being disabled until something is
 * actually different.
 *
 * NOBODY WRITES THIS LINE BUT THE OWNER (ruling R-0910-8, rule 3 void). The
 * box starts with X's own description, copied verbatim at creation and for
 * free, and every word after that is typed here. There is no "ask Nob to write
 * it" any more: summarising somebody's recent posts said what they posted last
 * week rather than who they are, and it billed per post read.
 *
 * THE SERVER OWNS THE RULES. This form checks only what it can check without
 * guessing, and leaves the lengths to the API, which prints its own sentence
 * beside the field that earned it. A client that duplicated those bounds would
 * eventually disagree with them, and the person would be arguing with a rule
 * nothing enforces.
 */

export const SAVE_LABEL = "Save";
export const SAVED_ACK = "Saved.";
/**
 * The bio (FR-3.8.1). Labelled by WHERE IT GOES rather than by what it is: this
 * is the only field in the room that a stranger reads, and "About you" would
 * have been a profile field on a settings screen. It sits beside *What gets
 * through* on the door — it does not replace it — and an empty box is a door
 * with no bio, which is a door that renders without one.
 */
export const BIO_LABEL = "Your bio";
export const BIO_HELP =
  "Shown on your door, beside what gets through. Leave it empty and your door shows none.";
/**
 * The read view's two words, and the quiet line under a door with nothing
 * written on it. It states the CONSEQUENCE rather than the absence — "no bio"
 * is a database fact, "your door shows none" is what a stranger sees.
 */
export const EDIT_LABEL = "Edit";
/** What it edits, for anybody reading the pane by its labels rather than by eye. */
export const EDIT_BIO_ARIA = "Edit your bio";
export const CANCEL_LABEL = "Cancel";
export const BIO_EMPTY = "Nothing written yet — your door shows no bio.";
/** The two content refusals, in the room's voice — the codes are the server's. */
export const BIO_TOO_LARGE_COPY = "That is longer than a bio is allowed to be.";
export const BIO_REFUSED_COPY = "Nob won’t put that on your door. Say it differently.";
export const DOORMAN_FACTS_NOTE =
  "Your doorman's name, tone and daily intake live in your House Rules — the “My doorman” section. Tell your doorman, or edit the rules.";

type Props = {
  settings: DoorSettings;
  busy: boolean;
  /** The server's refusals from the last save, by field. */
  issues: FieldIssue[];
  /** True for the couple of seconds after a save landed. */
  saved: boolean;
  onSave: (input: DoorSettingsInput) => void;
};

type Draft = { bio: string };

const FIELD =
  "h-9 rounded-lg border border-border bg-bg/60 px-3 text-sm text-text " +
  "focus-visible:border-border-loud focus-visible:outline-none focus-visible:ring-2 " +
  "focus-visible:ring-focus/40 disabled:opacity-50 aria-[invalid=true]:border-danger/60";

const LABEL = "text-[12px] uppercase tracking-[0.12em] text-faint";
const HELP = "text-[13px] leading-relaxed text-fainter";

function toDraft(s: DoorSettings): Draft {
  return { bio: s.bio ?? "" };
}

export function DoorSettingsForm({ settings, busy, issues, saved, onSave }: Props) {
  const [draft, setDraft] = useState<Draft>(() => toDraft(settings));
  /**
   * THE BOX IS SHUT UNTIL SOMEBODY ASKS FOR IT (owner item 13, 2026-08-26).
   *
   * An always-open textarea makes a settled sentence look unsettled: the one
   * line on this page a stranger reads was permanently mid-edit, which is the
   * wrong shape for something you write once and then check on. So the default
   * is a READING of the bio — the sentence as it is published, or the fact that
   * there is none — and Edit is what opens the form that was here before.
   *
   * It closes on the SERVER's acknowledgement rather than on the click, in the
   * effect below: a save the server refused (the bio's two content refusals) has
   * to leave the box open with the refusal beside it, and a view that closed
   * optimistically would swallow the one sentence explaining why nothing changed.
   */
  const [editing, setEditing] = useState(false);

  /**
   * The server's answer wins. A save comes back with what is now true — trimmed
   * persona strings included — and the form re-seeds from it, so the inputs
   * never keep showing a version of the door that nothing on the server agrees
   * with.
   */
  useEffect(() => {
    setDraft(toDraft(settings));
  }, [settings]);

  /** A save that landed closes the editor. A refused one does not (see above). */
  useEffect(() => {
    if (saved) setEditing(false);
  }, [saved]);

  const current = toDraft(settings);
  const dirty = draft.bio.trim() !== current.bio.trim();

  const issueFor = (field: string) => issues.find((i) => i.field === field)?.message ?? null;
  const bioIssue = issueFor("bio");

  function cancel() {
    setDraft(toDraft(settings));
    setEditing(false);
  }

  function submit() {
    if (!dirty || busy) return;
    // Only what moved is sent — and an emptied box is `null`, the instruction
    // "clear it", not an absence the server should ignore (FR-3.8.1).
    onSave({ bio: draft.bio.trim() ? draft.bio.trim() : null });
  }

  return (
    <form
      className="grid gap-6"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      {/* The one line on this screen a stranger reads (FR-3.8.1) — read, until
          Edit is pressed. */}
      <div>
        <p className={`${LABEL} block`} id="door-bio-label">
          {BIO_LABEL}
        </p>
        {editing ? (
          <>
            {/* Labelled BY the line above rather than by a second copy of the
                same two words: the reading and the editor are the same field. */}
            <textarea
              id="door-bio"
              rows={3}
              value={draft.bio}
              disabled={busy}
              autoFocus
              aria-labelledby="door-bio-label"
              aria-invalid={bioIssue !== null}
              aria-describedby={bioIssue ? "door-bio-error" : "door-bio-help"}
              onChange={(e) => setDraft((d) => ({ ...d, bio: e.target.value }))}
              className={`${FIELD} mt-1.5 block h-auto w-full resize-y py-2 leading-relaxed`}
            />
            {bioIssue ? (
              <p id="door-bio-error" role="alert" className="mt-1.5 text-[13px] text-danger">
                {bioIssue}
              </p>
            ) : (
              <p id="door-bio-help" className={`mt-1.5 ${HELP}`}>
                {BIO_HELP}
              </p>
            )}
          </>
        ) : (
          <>
            {/* The sentence as it is published — or, for a door with none, the
                consequence rather than the absence. The hook is for the tests:
                the same sentence is quoted elsewhere in this panel, and a room
                that finds it by its words would find the wrong copy. */}
            <p
              data-bio-read
              className={
                current.bio ? "mt-1.5 text-sm leading-relaxed text-text" : `mt-1.5 ${HELP} italic`
              }
            >
              {current.bio || BIO_EMPTY}
            </p>
            <p className="mt-2 flex flex-wrap items-baseline gap-x-3">
              {/* "Edit" is the word on screen; the accessible name says WHAT,
                  because this pane has more than one thing to edit. */}
              <LinkButton
                type="button"
                disabled={busy}
                aria-label={EDIT_BIO_ARIA}
                onClick={() => setEditing(true)}
              >
                {EDIT_LABEL}
              </LinkButton>
              {saved && (
                <span className="font-mono text-[11px] text-accent-ink/80" role="status">
                  {SAVED_ACK}
                </span>
              )}
            </p>
          </>
        )}
      </div>

      {/* The doorman's own facts moved into the House Rules (owner decision
          2026-08-05): one source, spoken or edited — never a second form. */}
      <p className={HELP}>{DOORMAN_FACTS_NOTE}</p>

      {/* Only while the box is open: a Save under a reading of the bio is a
          button for a change nobody is making. */}
      {editing && (
        <div className="flex items-center gap-3">
          <SecondaryButton type="submit" disabled={!dirty || busy}>
            {SAVE_LABEL}
          </SecondaryButton>
          {/* Discards what was typed outright. The door is unchanged either
              way; nothing here has been sent. */}
          <LinkButton type="button" disabled={busy} onClick={cancel}>
            {CANCEL_LABEL}
          </LinkButton>
        </div>
      )}
    </form>
  );
}
